import { useQueryClient, useMutation } from '@tanstack/react-query'

import { useAccessToken } from '@b-stock/fe-core/bstock-next/components'
import {
  getLocationClient,
  locationDetailsQuery,
  myLocationsQuery,
} from '@b-stock/fe-core/bstock-next/queries'
import { buildAxiosRequestConfig } from '@b-stock/fe-core/bstock-react'
import type {
  LocationsInput,
  PartialLocationUpdateDto,
} from '@b-stock/location-api-client'

export const useCreateLocation = (options?: { onSuccess?: () => void }) => {
  const accessToken = useAccessToken({ required: true })
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: LocationsInput) => {
      const client = getLocationClient()
      const { data } = await client.createLocation(
        { locationsInput: input },
        buildAxiosRequestConfig({ accessToken })
      )
      return data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [myLocationsQuery.name],
      })
      options?.onSuccess?.()
    },
  })
}

export const useUpdateLocation = (options?: {
  locationId?: string
  onSuccess?: () => void
}) => {
  const accessToken = useAccessToken({ required: true })
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      partialLocationUpdateDto,
      originalId,
    }: {
      id: string
      partialLocationUpdateDto: PartialLocationUpdateDto
      /** Used to recover the active version if `id` has been superseded. */
      originalId?: string
    }) => {
      const client = getLocationClient()
      const config = buildAxiosRequestConfig({ accessToken })
      try {
        const { data } = await client.updateLocation(
          { id, partialLocationUpdateDto },
          config
        )
        return data
      } catch (error) {
        // The location service versions locations: editing an address can mint a
        // new active version sharing the same `originalId`, leaving the `id` we
        // hold inactive. Mirror accounts-portal (api/location.ts updateAddress):
        // on the "isActive false" 403, look up the current active version by
        // originalId and patch that instead.
        const responseData = (
          error as {
            response?: { data?: { statusCode?: number; messages?: string } }
          }
        ).response?.data
        const isInactiveError =
          responseData?.statusCode === 403 &&
          typeof responseData.messages === 'string' &&
          responseData.messages.includes('isActive')

        if (isInactiveError && originalId) {
          const { data: locationsData } = await client.getLocations(
            { originalId, isActive: true, limit: 1 },
            config
          )
          const activeId = locationsData.locations[0]?.id
          if (activeId && activeId !== id) {
            const { data: retried } = await client.updateLocation(
              { id: activeId, partialLocationUpdateDto },
              config
            )
            return retried
          }
        }
        throw error
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [myLocationsQuery.name],
      })
      if (options?.locationId) {
        await queryClient.invalidateQueries({
          queryKey: [locationDetailsQuery.name, options.locationId],
        })
      }
      options?.onSuccess?.()
    },
  })
}
